import React, { useState, useCallback, useRef } from "react";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import {
  DocumentArrowUpIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { ExtractedIntakeData } from "./types";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { storage, functions } from "../../../../firebase";
import { useAccount } from "../../../../contexts/AccountContext";
import clsx from "clsx";

interface UploadStepProps {
  onExtracted: (data: ExtractedIntakeData, uploadedFileInfo?: { storagePath: string; fileName: string; downloadUrl: string }) => void;
  onSkip: () => void;
}

const UploadStep: React.FC<UploadStepProps> = ({ onExtracted, onSkip }) => {
  const { account } = useAccount();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
      } else {
        setError("Please upload a PDF file");
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
      } else {
        setError("Please upload a PDF file");
      }
    }
  }, []);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleProcess = useCallback(async () => {
    if (!selectedFile || !account?.id) return;

    setIsUploading(true);
    setIsProcessing(false);
    setError(null);
    setUploadProgress(0);

    try {
      // Create a unique path for the upload
      const timestamp = Date.now();
      const storagePath = `intake-forms/${account.id}/${timestamp}_${selectedFile.name}`;
      const storageRef = ref(storage, storagePath);

      // Upload the file
      setUploadProgress(30);
      await uploadBytes(storageRef, selectedFile);
      setUploadProgress(50);

      // Get the download URL for later use
      const downloadUrl = await getDownloadURL(storageRef);
      setUploadProgress(60);

      // Call the AI extraction function
      setIsUploading(false);
      setIsProcessing(true);

      const extractIntakeForm = httpsCallable<
        { storagePath: string },
        ExtractedIntakeData
      >(functions, "extractIntakeForm");

      const result = await extractIntakeForm({ storagePath });
      setUploadProgress(100);

      // Pass the extracted data and file info to the parent
      onExtracted(result.data, {
        storagePath,
        fileName: selectedFile.name,
        downloadUrl,
      });
    } catch (err: any) {
      console.error("Error processing intake form:", err);
      setError(
        err.message || "Failed to process the intake form. Please try again."
      );
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  }, [selectedFile, account?.id, onExtracted]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
        <div className="flex">
          <SparklesIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              AI-Powered Extraction
            </h3>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              Upload your intake form PDF and our AI will automatically extract parent,
              child, and case information. You can review and edit all extracted data
              before creating the case.
            </p>
          </div>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={clsx(
          "relative rounded-lg border-2 border-dashed p-8 text-center transition-colors",
          isDragging
            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
            : "border-zinc-300 dark:border-zinc-600 hover:border-zinc-400 dark:hover:border-zinc-500",
          (isUploading || isProcessing) && "pointer-events-none opacity-60"
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-3 rounded-lg bg-zinc-100 dark:bg-zinc-800 px-4 py-3">
                <DocumentArrowUpIcon className="h-8 w-8 text-blue-600" />
                <div className="text-left">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </div>

            {!isUploading && !isProcessing && (
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
              >
                Remove file
              </button>
            )}

            {(isUploading || isProcessing) && (
              <div className="space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {isUploading
                    ? "Uploading file..."
                    : "AI is extracting information..."}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-zinc-400" />
            <div>
              <Text className="text-zinc-600 dark:text-zinc-400">
                Drag and drop your intake form PDF here, or{" "}
                <button
                  type="button"
                  onClick={handleBrowseClick}
                  className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  browse files
                </button>
              </Text>
              <Text className="mt-1 text-xs text-zinc-500">
                Supported format: PDF (max 10MB)
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-between pt-4">
        <Button type="button" plain onClick={onSkip}>
          Skip & Enter Manually
        </Button>
        <Button
          type="button"
          onClick={handleProcess}
          disabled={!selectedFile || isUploading || isProcessing}
        >
          {isUploading
            ? "Uploading..."
            : isProcessing
            ? "Processing..."
            : "Extract Information"}
        </Button>
      </div>
    </div>
  );
};

export default UploadStep;
